import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'

import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import { reportRangeSearchSchema } from '@/features/reports/components/report-range-search'
import { StaffReportPage } from '@/features/reports/pages/staff-report'
import { requireCapability } from '@/lib/auth'

import type { ReportRangeSelection, StaffReportState } from '@/types'

const PAGE_SIZE = 50

/* Reports → Staff. The range, the tab, the person open in the side panel and
   the log's filters live in the URL, so "Jake's voids last month" can be
   shared with the owner. */
const searchSchema = reportRangeSearchSchema.extend({
  tab: z.enum(['summary', 'performance', 'loss', 'compliance', 'details']).optional(),
  person: z.string().optional(),
  log: z.enum(['shifts', 'drawers', 'cash', 'approvals', 'events']).optional(),
  who: z.string().optional(),
  kind: z.string().optional(),
  flagged: z.boolean().optional(),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(200).optional(),
})

export const Route = createFileRoute('/_dashboard/reports/staff')({
  beforeLoad: requireCapability('VIEW_REPORTS'),
  validateSearch: searchSchema,
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: StaffReportRoute,
})

function StaffReportRoute() {
  const { tab, person, log, who, kind, flagged, page, limit, ...range } = Route.useSearch()
  const navigate = Route.useNavigate()
  // Staff patterns need a few weeks to show: default to the last 30 days.
  const selection: ReportRangeSelection = range.mode === 'preset' && !range.preset ? { ...range, preset: '30d' } : range
  const state: StaffReportState = { tab: tab ?? 'summary', person, log: log ?? 'events', who, kind, flagged, page: page ?? 1, limit: limit ?? PAGE_SIZE }
  return (
    <StaffReportPage
      selection={selection}
      onSelectionChange={(next) => navigate({ search: (prev) => ({ ...prev, ...next, page: undefined }), replace: true })}
      state={state}
      onStateChange={(next) =>
        navigate({
          search: (prev) => ({
            ...prev,
            tab: next.tab === 'summary' ? undefined : next.tab,
            person: next.person,
            log: next.log === 'events' ? undefined : next.log,
            who: next.who,
            kind: next.kind,
            flagged: next.flagged || undefined,
            page: next.page > 1 ? next.page : undefined,
            limit: next.limit !== PAGE_SIZE ? next.limit : undefined,
          }),
          replace: true,
        })
      }
    />
  )
}
